import React, { useContext, useEffect, useState } from 'react'
import { DateTime } from 'luxon'

import { digObject, sortArrayBy } from '@domain-group/javascript-utils'

import { useRealtimeSubscription, useSetState } from '@domain-group/react-hooks'

import api from '@functions/api'

import useCurrentUser from '@hooks/useCurrentUser'

import RealtimeContext from '@contexts/realtimeContext'

import { NotificationModel, UserModel } from '@models/types'
import { shouldDisplayNotification } from '@functions/notification'

type AblyPayload = {
  payload: NotificationModel,
}

type DefaultState = {
  seenNotificationIds: number[],
}

type SetNotifications = React.Dispatch<React.SetStateAction<NotificationModel[]>>

type SetState = (state: Partial<DefaultState>) => void

const fetchNotifications = (setNotifications: SetNotifications) => {
  const threeDaysAgo = DateTime.now().minus({ days: 3 })

  const requestOptions = {
    created_date_start: threeDaysAgo.toFormat('yyyy-MM-dd'),
    include_notification_creator_logo: true,
    include_notification_subject: true,
    location: 'list',
  }

  api('/notifications.json', requestOptions)
    .then(({ data }: { data: NotificationModel[] }) => {
      setNotifications(data)
    })
    .catch((json) => {
      console.error(json.errors)
    })
}

const setupFromLocalStorage = (seenNotificationsKey: string, setState: SetState) => {
  if (!seenNotificationsKey) return

  const seenNotifications = localStorage.getItem(seenNotificationsKey)

  setState({
    seenNotificationIds: seenNotifications ? JSON.parse(seenNotifications) : [],
  })
}

type AddNotificationParams = {
  currentUser: UserModel,
  data: AblyPayload,
  setNotifications: SetNotifications,
  roleTitle: string,
}

const addNotification = (params: AddNotificationParams) => {
  const {
    currentUser, data, setNotifications, roleTitle,
  } = params

  const { payload: notification } = data
  const location = digObject(notification, 'options.location')

  if (!location && shouldDisplayNotification({ currentUser, notification, roleTitle })){
    setNotifications(prevNotifications => [...prevNotifications, notification])
  }
}

type CountUnreadNotificationsParams = {
  currentUser: UserModel,
  notifications: NotificationModel[],
  seenNotificationIds: number[],
}

const countUnreadNotifications = (params: CountUnreadNotificationsParams) => {
  const { currentUser, notifications, seenNotificationIds } = params

  if (!currentUser.id) return 0

  const unreadNotifications = notifications.filter(notification => (
    seenNotificationIds.indexOf(notification.id) === -1
  ))

  return unreadNotifications.length
}

type PerformToggleNotificationsParams = {
  notifications: NotificationModel[],
  seenNotificationIds: number[],
  seenNotificationsKey: string,
  setState: SetState,
}

const performToggleNotifications = (params: PerformToggleNotificationsParams) => {
  const {
    notifications, seenNotificationIds, seenNotificationsKey, setState,
  } = params

  let updatedSeenNotificationIds = [...seenNotificationIds]

  notifications.forEach((notification) => {
    if (updatedSeenNotificationIds.indexOf(notification.id) === -1){
      updatedSeenNotificationIds.push(notification.id)
    }
  })

  // Don't store more than 100 seen notifications
  if (updatedSeenNotificationIds.length > 100){
    updatedSeenNotificationIds = updatedSeenNotificationIds.slice(-100)
  }

  localStorage.setItem(seenNotificationsKey, JSON.stringify(updatedSeenNotificationIds))

  setState({ seenNotificationIds: updatedSeenNotificationIds })
}

const filterAndSortNotifications = (notifications: NotificationModel[]) => {
  const sorted = sortArrayBy(notifications, 'desc', 'id')
  return sorted.filter(notification => !notification.read)
}

const defaultState: DefaultState = {
  seenNotificationIds: [],
}

const useNotificationsList = () => {
  const [state, setState] = useSetState(defaultState)
  const { seenNotificationIds } = state

  const [notifications, setNotifications] = useState<NotificationModel[]>([])

  const { currentUser, roleTitle } = useCurrentUser()
  const seenNotificationsKey = `seenNotifications_user_${currentUser.id}`

  useEffect(() => fetchNotifications(setNotifications), [])
  useEffect(() => setupFromLocalStorage(seenNotificationsKey, setState), [currentUser.id])

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  // Realtime/Websocket Connection
  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: AblyPayload) => addNotification({
        // @ts-expect-error TS2322
        currentUser, data, setNotifications, roleTitle,
      }),
    },
    channelName: 'notifications',
    clientRef,
    eventName: 'system.notification',
  }, [currentUser.id])

  // @ts-expect-error TS2322
  const unseenNotificationsCount = countUnreadNotifications({ currentUser, notifications, seenNotificationIds })

  const filteredNotifications = filterAndSortNotifications(notifications)

  return {
    callbacks: {
      performToggleNotifications: () => performToggleNotifications({
        notifications, seenNotificationIds, seenNotificationsKey, setState,
      }),
    },
    hasNotifications: !!notifications.length,
    notifications: filteredNotifications,
    unseenNotificationsCount,
  }
}

export default useNotificationsList
